// ===========================================
// Account Deletion Hooks
// ===========================================
// React Query hooks for account deletion functionality

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDeletionBlockers,
  getDeletionSummary,
  requestAccountDeletion,
  cancelAccountDeletion,
  exportUserData,
  deleteChildAccount,
  RequestDeletionRequest,
  DeleteChildRequest,
} from '../api/user.api';

// ===========================================
// QUERY KEYS
// ===========================================

export const deletionKeys = {
  all: ['deletion'] as const,
  blockers: () => [...deletionKeys.all, 'blockers'] as const,
  summary: () => [...deletionKeys.all, 'summary'] as const,
};

// ===========================================
// QUERIES
// ===========================================

/**
 * Hook to fetch deletion blockers
 */
export function useDeletionBlockers() {
  return useQuery({
    queryKey: deletionKeys.blockers(),
    queryFn: getDeletionBlockers,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to fetch deletion summary
 */
export function useDeletionSummary(enabled = true) {
  return useQuery({
    queryKey: deletionKeys.summary(),
    queryFn: getDeletionSummary,
    enabled,
    staleTime: 30000, // 30 seconds
  });
}

// ===========================================
// MUTATIONS
// ===========================================

/**
 * Hook to request account deletion
 */
export function useRequestDeletion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RequestDeletionRequest) => requestAccountDeletion(data),
    onSuccess: () => {
      // Invalidate deletion-related queries
      queryClient.invalidateQueries({ queryKey: deletionKeys.all });
    },
  });
}

/**
 * Hook to cancel account deletion
 */
export function useCancelDeletion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelAccountDeletion,
    onSuccess: () => {
      // Invalidate deletion-related queries
      queryClient.invalidateQueries({ queryKey: deletionKeys.all });
    },
  });
}

/**
 * Hook to export user data
 */
export function useExportUserData() {
  return useMutation({
    mutationFn: exportUserData,
    onSuccess: (result: { blob: Blob; schoolName: string }) => {
      // Generate filename: [school-name]-data-export-[yyyy-mm-dd].json
      const dateStr = new Date().toISOString().split('T')[0];
      const schoolSlug = result.schoolName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      const filename = `${schoolSlug}-data-export-${dateStr}.json`;

      // Trigger download
      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}

/**
 * Hook to delete a child account (parent only)
 */
export function useDeleteChildAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ studentId, data }: { studentId: string; data: DeleteChildRequest }) =>
      deleteChildAccount(studentId, data),
    onSuccess: () => {
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: deletionKeys.all });
      queryClient.invalidateQueries({ queryKey: ['students'] });
      queryClient.invalidateQueries({ queryKey: ['family'] });
    },
  });
}
